import { useCallback, useEffect, useRef, useState, useMemo } from 'react'
import { Modal } from 'react-native'
import { SafeAreaProvider } from 'react-native-safe-area-context'
import { useDispatch, useSelector } from 'APP/Hooks/redux'
import debounce from 'lodash/debounce'

import { isWeb } from 'APP/Helpers/checkPlatform'
import { semanticSearchActions } from 'APP/Store/SemanticSearch'
import { SemanticSearchRequest } from 'APP/Store/SemanticSearch/types'

import { getNormalizedSearchResults, MIN_SEARCH_TEXT_LENGTH } from './helpers'
import { DisplayResult, Props } from './types'
import ModalContent from './ModalContent'
import { WebContainer, WebBackdrop, WebContentWrapper, MobileFullScreenContainer } from './styles'
import Analytics from 'APP/Services/Analytics'

const DEBOUNCE_TIME = 400

/**
 * Data Structure:
 * - L1 (Level 1): Parent symptom categories (e.g., "Ear, Nose & Throat", "Respiratory")
 * - L2 (Level 2): Specific symptoms nested under L1 (e.g., "Ear pain or discharge", "Fever")
 *
 * Flow:
 * 1. User types a symptom query
 * 2. Query is sent to smartsearch API (via searchSource URL), only if the query's length >= 3 using debounce
 * 3. Smartsearch API returns matching choice IDs (searchResults)
 * 4. Results are normalized (normalizedResults fn) by matching choice IDs (L2) to their L1 parent for display
 *
 * @param isOpen - Controls modal visibility (defaults to false)
 * @param onClose - Callback when modal is closed
 * @param searchSource - URL endpoint for the smartsearch API, provided by BM.
 * @param groupL1Categories - L1 categories, each containing display name and payload (from BM).
 * @param choices - All available L2 choices, each with id, display name, group_id (L1 parent), and payload (from BM)
 */
const SemanticSearch = ({
  isOpen = false,
  onClose,
  searchSource,
  groupL1Categories,
  choices,
  onSearchChoiceSelected,
}: Props) => {
  const [searchText, setSearchText] = useState('')
  const dispatch = useDispatch()
  const { results: searchResults, loading, error } = useSelector((state) => state?.semanticSearch)

  // Ref always points to the latest dispatch/searchSource without resetting the debounce timer
  const searchCallbackRef = useRef((query: string) => {
    dispatch(
      semanticSearchActions.runSemanticSearch({
        query,
        source: searchSource,
      } as SemanticSearchRequest)
    )
  })
  searchCallbackRef.current = (query: string) => {
    dispatch(
      semanticSearchActions.runSemanticSearch({
        query,
        source: searchSource,
      } as SemanticSearchRequest)
    )
  }

  const debouncedSearch = useRef(
    debounce((query: string) => searchCallbackRef.current(query), DEBOUNCE_TIME)
  ).current

  const handleTextChange = useCallback(
    (text: string) => {
      setSearchText(text)
      if (text.length > MIN_SEARCH_TEXT_LENGTH) {
        debouncedSearch(text)
      } else {
        dispatch(semanticSearchActions.reset())
      }
    },
    [debouncedSearch]
  )

  const onRetry = useCallback(() => {
    if (searchText.length > MIN_SEARCH_TEXT_LENGTH) {
      debouncedSearch(searchText)
    }
  }, [debouncedSearch, searchText])

  useEffect(
    () => () => {
      debouncedSearch.cancel()
    },
    [debouncedSearch]
  )

  useEffect(() => {
    if (!isOpen) {
      setSearchText('')
      dispatch(semanticSearchActions.reset())
    }
  }, [isOpen, dispatch])

  useEffect(() => {
    if (isOpen) {
      Analytics.trackScreen('Semantic Search')
    }
  }, [isOpen])

  const normalizedResults = useMemo(
    () => getNormalizedSearchResults(groupL1Categories, choices, searchResults),
    [groupL1Categories, choices, searchResults]
  )

  const onResultPress = useCallback(
    (item: DisplayResult) => {
      const choice = choices.find((c) => c.id === item.id)
      if (choice) {
        onSearchChoiceSelected(choice)
      }
    },
    [choices, onSearchChoiceSelected]
  )

  const modalContent = (
    <ModalContent
      searchText={searchText}
      onChangeText={handleTextChange}
      onClose={onClose}
      loading={loading}
      onRetry={onRetry}
      error={error}
      normalizedResults={normalizedResults}
      onResultPress={onResultPress}
    />
  )

  return (
    <Modal
      visible={isOpen}
      animationType={isWeb() ? 'fade' : 'slide'}
      transparent={isWeb()}
      statusBarTranslucent
      onRequestClose={() => {
        Analytics.trackEvent('button_click', {
          button_value: 'Semantic Search Close - Hardware Key',
        })
        onClose()
      }}
    >
      <SafeAreaProvider>
        {isWeb() ? (
          <WebContainer>
            <WebBackdrop
              testID="SEMANTIC_SEARCH_BACKDROP"
              onPress={() => {
                Analytics.trackEvent('button_click', {
                  button_value: 'Semantic Search Close - Backdrop',
                })
                onClose()
              }}
            />
            <WebContentWrapper>{modalContent}</WebContentWrapper>
          </WebContainer>
        ) : (
          <MobileFullScreenContainer>{modalContent}</MobileFullScreenContainer>
        )}
      </SafeAreaProvider>
    </Modal>
  )
}

export default SemanticSearch
